import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * "Leuk met"-upsell op de productpagina.
 * Aanvinken voegt het product direct toe aan de winkelwagen, uitvinken haalt het weer weg.
 * Meldt elke wijziging via Horizon's CartLinesUpdateEvent, zodat het winkelwagenicoon
 * en de cart drawer meteen bijwerken.
 */
class MmUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
    this.addEventListener('change', this.#onChange);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
    this.removeEventListener('change', this.#onChange);
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = event.target.closest('.mm-upsell-item');
    if (!item || !(item instanceof HTMLElement)) return;
    if (event.target.closest('a') || event.target.closest('.mm-upsell-variants')) return;

    const variantWrap = /** @type {HTMLElement | null} */ (item.querySelector('.mm-upsell-variants'));
    const select = /** @type {HTMLSelectElement | null} */ (item.querySelector('.mm-upsell-select'));

    if (item.classList.contains('is-checked')) {
      this.#setQuantity(item, item.dataset.activeVariant || item.dataset.variant || '', 0).then((ok) => {
        if (!ok) return;
        item.classList.remove('is-checked');
        delete item.dataset.activeVariant;
        if (variantWrap && select) {
          variantWrap.hidden = true;
          select.selectedIndex = 0;
        }
      });
      return;
    }

    if (item.dataset.hasVariants === 'true' && variantWrap) {
      variantWrap.hidden = !variantWrap.hidden;
      return;
    }

    this.#setQuantity(item, item.dataset.variant || '', 1).then((ok) => {
      if (!ok) return;
      item.classList.add('is-checked');
      item.dataset.activeVariant = item.dataset.variant;
    });
  };

  /** @param {Event} event */
  #onChange = async (event) => {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement) || !select.classList.contains('mm-upsell-select')) return;
    const item = select.closest('.mm-upsell-item');
    if (!(item instanceof HTMLElement) || !select.value) return;

    const option = select.options[select.selectedIndex];
    const priceEl = item.querySelector('.mm-upsell-price');
    if (priceEl && option?.dataset.price) priceEl.textContent = `+ ${option.dataset.price}`;

    const previousId = item.dataset.activeVariant;
    if (previousId && previousId !== select.value) {
      await this.#setQuantity(item, previousId, 0);
    }
    if (previousId !== select.value && (await this.#setQuantity(item, select.value, 1))) {
      item.dataset.activeVariant = select.value;
      item.classList.add('is-checked');
    }
  };

  /**
   * Zet het aantal van een variant in de winkelwagen en informeert de rest van het thema.
   * @param {HTMLElement} item
   * @param {string} variantId
   * @param {number} quantity
   * @returns {Promise<boolean>} of de wijziging gelukt is
   */
  async #setQuantity(item, variantId, quantity) {
    if (!variantId) return false;
    item.classList.add('is-busy');

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : null))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'product',
        lines: [{ merchandiseId: variantId, quantity }],
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(`${window.Shopify?.routes?.root ?? '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          updates: { [variantId]: quantity },
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      if (!response.ok) throw new Error('Cart update failed');
      const cart = await response.json();

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          sections: cart.sections,
          source: 'mm-upsell',
          itemCount: cart.item_count,
          didError: false,
        },
      });
      return true;
    } catch (error) {
      deferred.reject(error);
      return false;
    } finally {
      item.classList.remove('is-busy');
    }
  }
}

if (!customElements.get('mm-upsell')) {
  customElements.define('mm-upsell', MmUpsell);
}
